import React, { useEffect, useRef, useState } from 'react';

import { animated, useSpring } from 'react-spring';

import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import ProgressBar from 'views/ProgressBar.js';
import Utils, { useSize } from 'files/Utils.js';
import Views, { AltBadge } from 'views/Main.js';

export const CalloutIndex = 4950;
export const EndIndex = 4990;
export const FrontIndex = EndIndex + 10;
export const InlineMenuIndex = 4550;
export const LayerItemSpacing = 25;

const Layer = React.forwardRef((props, ref) => {

    const paddingVal = 12;
    const { buttons, children, header, id, index, options = {}, title, utils } = props;
    const { layerState, loading, onActionChange, onClose, onPreparingClose, position, removeOverflow, removePadding, shouldRequestClose, sizing, sticky, zIndex } = options;

    const buttonContainerRef = useRef(null);
    const headerRef = useRef(null);
    const layerRef = useRef(null);

    const [animations, setAnimations] = useSpring(() => ({
        top: window.innerHeight,
        config: { mass: 1, tension: 180, friction: 18 }
    }));
    const contentSize = useSize(layerRef);
    const [focusState, setFocusState] = useState(true);
    const [height, setHeight] = useState(0);
    const [layerPosition, setLayerPosition] = useState(position);
    const [onHover, setOnHover] = useState(null);
    const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const [zIndexVal, setZIndexVal] = useState(zIndex);

    const onLayerAction = ({ detail }) => {
        if(detail.layerID === id) {
            onSetLayerState(detail.action);
        }
    }

    const onLayerFocusChange = ({ detail }) => {
        onUpdateFocusState(detail === id);
    }

    const onMouseEnter = () => {
        document.body.style.overflowY = 'hidden';
    }

    const onMouseLeave = () => {
        document.body.style.overflowY = 'scroll';
    }

    const onRequestLayerClose = bypass => {
        if(bypass === true || shouldRequestClose === false) {
            onSetLayerState('close');
            return;
        }
        let modified = ref && ref.current && ref.current.user_interaction === true;
        if(!modified) {
            onSetLayerState('close');
            return;
        }
        utils.alert.show({
            title: `Close Layer`,
            message: 'Are you sure that you want to close this layer? All unsaved changes will be lost.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Do Not Close',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetLayerState('close');
                    return;
                }
            }
        })
    }

    const onSetLayerState = action => {
        if(action === 'close') {
            setAnimations({ top: window.innerHeight });
            document.body.style.overflowY = 'scroll';

            if(typeof(onPreparingClose) === 'function') {
                onPreparingClose();
            }
            if(typeof(onClose) === 'function') {
                setTimeout(onClose.bind(this, id), 500);
            }
            return;
        }
    }

    const onSetLayerPosition = async () => {
        try {
            if(!layerRef.current) {
                return;
            }
            setHeight(contentSize.height);
            setAnimations({ top: (size.height / 2) - (contentSize.height / 2) });
        } catch(e) {
            console.error(e.message);
        }
    }

    const onUpdateFocusState = val => {
        setFocusState(val);
        if(ref) {
            ref.current = {
                ...ref.current,
                focused: val
            }
        }
    }

    const onWindowSizeChange = () => {
        setSize({
            width: window.innerWidth,
            height: window.innerHeight
        });
    }

    const getDefaultPosition = () => {
        if(layerPosition) {
            return layerPosition;
        }
        return {
            x: (size.width / 2) - (getLayerWidth() / 2),
            y: (size.height / 2) - (getLayerHeight() / 2)
        }
    }

    const getHeaderContent = () => {
        return (
            <div style={{
                position: 'relative'
            }}>
                <div
                ref={headerRef}
                style={Appearance.styles.header()}>
                    {header}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        <div style={{
                            width: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            paddingLeft: 4
                        }}>
                            <AltBadge
                            utils={utils}
                            onClick={onRequestLayerClose.bind(this, true)}
                            style={{
                                width: 75
                            }}
                            content={{
                                text: 'Close',
                                color: Appearance.colors.red
                            }}
                            shortcut={{
                                focused: focusState,
                                command: 'escape',
                                onActionTriggered: onRequestLayerClose
                            }} />
                        </div>

                        <span style={{
                            ...Utils.isMobile() === false && Appearance.textStyles.heavyTitle(),
                            ...Utils.isMobile() === true && Appearance.textStyles.layerItemTitle(),
                            flexGrow: 1,
                            textAlign: 'center',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            paddingLeft: 12,
                            paddingRight: 12
                        }}>{title}</span>

                        <div style={{
                            width: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'center'
                        }} />
                    </div>
                </div>
                {loading === true && (
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 2,
                        overflow: 'hidden',
                        borderRadius: 1
                    }}>
                        <ProgressBar/>
                    </div>
                )}
            </div>
        )
    }

    const getLayerHeight = () => {
        return window.innerHeight - (buttonContainerRef.current ? buttonContainerRef.current.clientHeight : 0) - (headerRef.current ? headerRef.current.clientHeight : 0) - (paddingVal * 2) - 40;
    }

    const getLayerWidth = () => {
        if(Utils.isMobile() === true) {
            return window.innerWidth - 24;
        }
        let width = getLayerSizingWidth(sizing);
        return size.width > width ? width : size.width - 60;
    }

    const getPosition = () => {
        if(layerPosition) {
            return layerPosition;
        }
        return {
            x: (size.width / 2) - (getLayerWidth() / 2),
            y: paddingVal
        }
    }

    useEffect(() => {
        if(ref && ref.current) {
            ref.current.id = id;
        }
    }, [ref]);

    useEffect(() => {
        if(contentSize) {
            onSetLayerPosition();
        }
    }, [contentSize]);

    useEffect(() => {
        setZIndexVal(zIndex);
    }, [options]);

    useEffect(() => {
        if(layerState === 'close') {
            document.body.style.overflowY = 'scroll';
            setAnimations({ top: window.innerHeight });
            if(typeof(onPreparingClose) === 'function') {
                onPreparingClose();
            }
            if(typeof(onClose) === 'function') {
                setTimeout(onClose.bind(this, id), 500);
            }
            return;
        }
        if(typeof(onActionChange) === 'function') {
            onActionChange({
                action: layerState,
                height: getLayerHeight()
            })
        }

    }, [layerState]);

    useEffect(() => {
        if(position) {
            setLayerPosition({
                y: position.y,
                x: Utils.isMobile() ? paddingVal : position.x
            })
        }
    }, [position]);

    useEffect(() => {

        utils.events.on('layer_action_change', onLayerAction);
        utils.events.on('layer_focus_change', onLayerFocusChange);
        window.addEventListener('resize', onWindowSizeChange);

        return () => {
            utils.events.off('layer_action_change', onLayerAction);
            utils.events.off('layer_focus_change', onLayerFocusChange);
            window.removeEventListener('resize', onWindowSizeChange);
        }
    }, []);

    return (
        <div
        id={id}
        style={{
            position: 'fixed',
            zIndex: zIndexVal,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
        }}>
            <animated.div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                position: 'fixed',
                width: getLayerWidth(),
                height: 'auto',
                zIndex: zIndexVal,
                overflow: removeOverflow ? 'visible' : 'hidden',
                boxShadow: '0px 0px 50px rgba(0,0,0,0.2)',
                borderRadius: 20,
                backgroundColor: Appearance.colors.layer.background(),
                top: animations.top
            }}>
                <div
                ref={layerRef}
                style={{
                    flexGrow: 1,
                    padding: 0,
                    margin: 0
                }}>
                    {getHeaderContent()}
                    <div style={{
                        position: 'relative'
                    }}>
                        {sticky && sticky.top}
                        <div style={{
                            height: '100%',
                            overflowX: 'hidden',
                            overflowY: removeOverflow ? 'visible' : 'scroll',
                            padding: removePadding ? 0 : 12,
                            maxHeight: getLayerHeight(),
                        }}>
                            {children}
                        </div>
                        {sticky && sticky.bottom}
                        {buttons && (
                            <div
                            ref={buttonContainerRef}
                            style={{
                                padding: 12,
                                borderTop: `1px solid ${Appearance.colors.divider()}`
                            }}>
                                <div className={'row p-0 m-0'}>
                                    {buttons.filter(b => {
                                        return b.visible !== false;
                                    }).map((button, index, buttons) => {
                                        let paddingX = 'px-0';
                                        if(buttons.length === 2) {
                                            paddingX = index === 0 ? 'pl-0 pr-1' : 'pl-1 pr-0';
                                        } else if(buttons.length > 2) {
                                            paddingX = index === 1 ? 'px-2' : 'px-0';
                                        }
                                        return (
                                            <div
                                            key={index}
                                            className={`col-${parseInt(12 / buttons.length)} py-0 ${paddingX}`}>
                                                <Button
                                                {...button}
                                                utils={utils}
                                                loading={button.loading || loading === button.key} />
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </animated.div>
        </div>
    )
});

export const LayerItemArrow = ({ className = '', collapsed, color, onClick, style }) => {

    const [_collapsed, _setCollapsed] = useState(collapsed);
    const [animations, setAnimations] = useSpring(() => ({
        transform: `rotate(${collapsed ? '0deg' : '180deg'})`,
        config: { mass: 1, tension: 180, friction: 16 }
    }));

    const onCollapseClick = evt => {
        let nextCollapse = !_collapsed;
        if(typeof(onClick) === 'function') {
            onClick(nextCollapse, evt);
        }
        _setCollapsed(nextCollapse);
    }

    const getImage = () => {
        switch(color) {
            case Appearance.colors.primary():
            return `images/down-arrow-blue-small.png`;

            default:
            return 'images/down-arrow-grey-small.png';
        }
    }

    useEffect(() => {
        _setCollapsed(collapsed);
    }, [collapsed]);

    useEffect(() => {
        setAnimations({ transform: `rotate(${_collapsed ? '0deg' : '180deg'})` });
    }, [_collapsed]);

    return (
        <div
        className={className}
        syle={{
            width: 15,
            height: 15,
            ...style
        }}>
            <animated.img
            className={'text-button'}
            onClick={onCollapseClick}
            src={getImage()}
            style={{
                width: 15,
                height: 15,
                objectFit: 'contain',
                ...style,
                ...animations
            }} />
        </div>
    )
}

export const LayerItem = ({ badge, children, childrenStyle, collapsed, headerStyle, lastItem, onVisibilityChange, required, rightContent, shouldStyle, style, subTitle, title, utils }) => {

    const [_collapsed, _setCollapsed] = useState(collapsed);
    const [animations, setAnimations] = useSpring(() => ({
        opacity: collapsed ? 0 : 1,
        maxHeight: collapsed ? 0 : 1500,
        config: { mass: 1, tension: 180, friction: 30 }
    }));

    const onCollapseClick = () => {
        let next = !_collapsed;
        _setCollapsed(next);
        if(typeof(onVisibilityChange) === 'function') {
            onVisibilityChange(next);
        }
    }

    const isCollapseEnabled = () => {
        return typeof(_collapsed) === 'boolean' ? true : false;
    }

    const getAnimationsStyles = () => {
        if(isCollapseEnabled()) {
            return {
                overflowX: 'hidden',
                overflowY: 'scroll',
                ...animations
            };
        }
        return null;
    }

    const getCollapseStyles = () => {
        if(!isCollapseEnabled() || _collapsed === false) {
            return {
                marginBottom: lastItem ? 0 : LayerItemSpacing
            };
        }
        if(lastItem !== true) {
            return {
                paddingBottom: 2,
                marginBottom: _collapsed ? 10 : LayerItemSpacing,
                borderBottom: `1px solid ${Appearance.colors.divider()}`,
            }
        }
        return null;
    }

    useEffect(() => {
        setAnimations({
            opacity: _collapsed ? 0 : 1,
            maxHeight: _collapsed ? 0 : 1500
        });
    }, [_collapsed]);

    useEffect(() => {
        _setCollapsed(collapsed);
    }, [collapsed]);

    return (
        <div style={{
            width: '100%',
            ...getCollapseStyles(),
            ...style
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                textAlign: 'left'
            }}>
                {required && (
                    <div style={{
                        width: 8,
                        height: 8,
                        minWidth: 8,
                        minHeight: 8,
                        borderRadius: 4,
                        overflow: 'hidden',
                        backgroundColor: Appearance.colors.red,
                        marginRight: 8
                    }} />
                )}
                <div style={{
                    display: 'block',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    jusitfyContent: 'space-between',
                    alignItems: 'center',
                    minWidth: 0,
                    ...headerStyle
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                    }}>
                        <span style={{
                            ...Appearance.textStyles.layerItemTitle(),
                            display: 'block',
                            lineHeight: 1.5
                        }}>{title}</span>
                        {typeof(subTitle) === 'string' && (
                            <span style={{
                                display: 'block',
                                color: Appearance.colors.subText(),
                                fontWeight: 600,
                                fontSize: 14,
                                lineHeight: 1.25
                            }}>{subTitle}</span>
                        )}
                    </div>
                    {badge && (
                        <AltBadge
                        utils={utils}
                        content={badge}
                        style={{
                            top: 0,
                            marginLeft: 8,
                            marginRight: 0
                        }} />
                    )}
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}>
                    {rightContent}
                    {isCollapseEnabled() && (
                        <LayerItemArrow
                        collapsed={_collapsed}
                        onClick={onCollapseClick} />
                    )}
                </div>
            </div>
            <animated.div style={{
                width: '100%',
                marginTop: 8,
                ...shouldStyle !== false && {
                    ...Appearance.styles.unstyledPanel(),
                    borderRadius: 15
                },
                ...childrenStyle,
                ...getAnimationsStyles()
            }}>
                {children}
            </animated.div>
        </div>
    )
}

export const getLayerSizingWidth = sizing => {
    switch(sizing) {
        case 'extra_small':
        return 300;

        case 'small':
        return 400;

        case 'medium':
        return 550;

        case 'extra_large':
        return window.innerWidth < 1000 ? window.innerWidth : 1000;

        case 'fullscreen':
        return window.innerWidth - 120;

        default:
        return 700;
    }
}

export default Layer;
